import { useCallback, useMemo, useRef } from 'react';
import { SubscriptionType } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/subscription_backfill_enums.pb';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { useEligibility } from '@frontend/user-preferences';
import { useNavigate } from '@frontend/weave-router';
import { CALL_INTEL_BASE_URL } from '../../constants';
import { backfillBannerPendoEvents, backfillBannerTrackingIds, trackBackfillBannerEvent } from '../../tracking-ids';

const CALL_HISTORY_URL = '/calls/call-history';

interface Batch2GlobalBannerProps {
  aggregatedSubscriptionType: SubscriptionType;
  backfillDays?: number;
  locationCount: number;
  isPartial: boolean;
  onCallIntelligenceRoute: boolean;
  onCallHistoryRoute: boolean;
}

export const Batch2GlobalBanner = ({
  aggregatedSubscriptionType,
  backfillDays,
  locationCount,
  isPartial,
  onCallIntelligenceRoute,
  onCallHistoryRoute,
}: Batch2GlobalBannerProps) => {
  const { t } = useTranslation('call-intelligence');
  const navigate = useNavigate();

  const hasFiredPendoRef = useRef(false);

  const { shouldShow, recordDismiss } = useEligibility({
    featureId: 'call-intel-backfill-batch2-banner',
    defaultCriteria: {
      type: 'dismiss-cooldown',
      maxDismisses: 99999,
      cooldownDays: 7,
    },
  });

  const isBase = aggregatedSubscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_BASE;

  const action = useMemo(() => {
    if (isBase) {
      return {
        label: t('Go to Call History'),
        onClick: () => {
          recordDismiss();
          navigate({ to: CALL_HISTORY_URL as any });
        },
      };
    }
    return {
      label: t('Go to Call Intelligence'),
      onClick: () => {
        recordDismiss();
        navigate({ to: CALL_INTEL_BASE_URL });
      },
    };
  }, [isBase, navigate, recordDismiss, t]);

  const title = useMemo(() => {
    if (aggregatedSubscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_TRIAL)
      return t('Your Call Intelligence Insights are Ready!');
    return t('Your Call Intelligence Data is Ready!');
  }, [aggregatedSubscriptionType, t]);

  const message = useMemo(() => {
    if (isPartial) {
      return t(
        'Your call insights are ready for {{count}} locations. Dive into patient needs, outcomes, and next steps to make every conversation count.',
        { count: locationCount }
      );
    }

    if (aggregatedSubscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_TRIAL) {
      return t(
        'Your last {{backfillDays}} days of call insights are ready so you can start seeing trends and opportunities. Dive into patient needs, outcomes, and next steps to make every conversation count.',
        { backfillDays }
      );
    }
    if (isBase) {
      return t(
        'Your last {{backfillDays}} days of Call Intelligence is ready so you can start reviewing past summaries, transcripts, and AI-powered insights. Dive into patient needs, outcomes, and next steps to make every conversation count.',
        { backfillDays }
      );
    }
    // PRO
    return t(
      'Your last {{backfillDays}} days of call insights are ready so you can start seeing trends and opportunities. Dive into patient needs, outcomes, and next steps to make every conversation count.',
      { backfillDays }
    );
  }, [aggregatedSubscriptionType, isBase, isPartial, backfillDays, locationCount, t]);

  // Hidden on the relevant route
  const hiddenOnRoute = isBase ? onCallHistoryRoute : onCallIntelligenceRoute;

  const handleVisible = useCallback(() => {
    if (!hasFiredPendoRef.current) {
      hasFiredPendoRef.current = true;
      trackBackfillBannerEvent(backfillBannerPendoEvents.batch2BannerSeen, { aggregatedSubscriptionType, isPartial });
    }
  }, [aggregatedSubscriptionType, isPartial]);

  if (!shouldShow || hiddenOnRoute) return null;

  return (
    <GlobalBannerDynamic
      type='premium'
      surfaceId={isBase ? 'banner.call-intel-base-backfill' : 'banner.call-intel-backfill'}
      id='call-intel-backfill-batch2-ready'
      title={title}
      message={message}
      isDismissible
      onDismiss={recordDismiss}
      onVisible={handleVisible}
      action={action}
      trackingId={backfillBannerTrackingIds.batch2BannerCta}
      dismissBtnTrackingId={backfillBannerTrackingIds.batch2BannerDismiss}
    />
  );
};
